import { useState } from 'react';
import { MonoXOneSubmissionClient } from '@/lib/clients/monoXOne';
import { MonoXOneConfigForDB, MonoXOneMapping } from '@/types/structured-info';

export interface SubmissionResult {
  success: boolean;
  url?: string;
  error?: string;
  verified?: boolean;
  retryCount?: number;
  stage?: 'submitting' | 'verifying' | 'completed' | 'failed';
}

export const useMonoXOneSubmission = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<SubmissionResult | null>(null);
  const [currentStage, setCurrentStage] = useState<'submitting' | 'verifying' | 'completed' | 'failed'>('submitting');

  const submitToMonoXOne = async (
    config: MonoXOneConfigForDB | MonoXOneMapping,
    reportData: Record<string, unknown>,
    autoRedirect: boolean = true
  ): Promise<SubmissionResult> => {
    setIsSubmitting(true);
    setResult(null);
    setCurrentStage('submitting');

    try {
      // APIクライアントの初期化
      const client = new MonoXOneSubmissionClient(config.tenantName);
      
      console.log('🔐 MONO-X One認証開始...');
      
      // 認証情報の取得（DynamoDBから）
      // config がMonoXOneConfigForDB型の場合は、認証情報が含まれている
      if ('username' in config && 'password' in config) {
        const username = config.username;
        const password = config.password;
        
        if (!username || !password) {
          throw new Error('MONO-X One認証情報が不完全です。設定画面で認証情報を設定してください。');
        }
        
        // ログイン実行
        await client.login(username, password);
      } else {
        // 旧形式の場合は、DynamoDBから認証情報を取得
        const { getMonoXOneConfig } = await import('@/lib/clients/structured-info');
        const fullConfig = await getMonoXOneConfig(config.structuredInfoId);
        
        if (!fullConfig) {
        throw new Error('MONO-X One接続設定が見つかりません。設定画面で接続を設定してください。');
      }
      
        if (!('username' in fullConfig && 'password' in fullConfig)) {
        throw new Error('MONO-X One認証情報が不完全です。設定画面で認証情報を設定してください。');
      }
      
      // ログイン実行
        await client.login(fullConfig.username, fullConfig.password);
      }
      console.log('✅ MONO-X One認証成功');
      
      // データ送信
      const { pageUrl } = await client.submitReportData(config, reportData);
      
      // 送信完了、データ反映確認段階へ
      setCurrentStage('verifying');
      
      // 送信されたデータを取得（確認用）
      const normalizedConfig = (client as any).normalizeConfig(config);
      const mappedData = (client as any).mapReportDataToMonoXFields(
        normalizedConfig.fieldMappings,
        reportData
      );
      
      console.log('🔍 データ反映確認開始...');
      
      // データ反映確認を実行
      const { verified, retryCount } = await client.verifyDataReflection(
        pageUrl,
        mappedData,
        6 // 最大6回の段階的確認
      );
      
      setCurrentStage('completed');
      
      const successResult: SubmissionResult = {
        success: true,
        url: pageUrl,
        verified,
        retryCount,
        stage: 'completed'
      };
      
      setResult(successResult);
      
      // 自動リダイレクト
      if (autoRedirect && pageUrl) {
        console.log('🔄 自動的にMONO-X Oneページに遷移します...');
        // 少し待ってからリダイレクト（ユーザーが完了メッセージを確認できるように）
        setTimeout(() => {
          window.open(pageUrl, '_blank');
        }, 1500);
      }
      
      return successResult;
      
    } catch (error) {
      setCurrentStage('failed');
      
      const errorResult: SubmissionResult = {
        success: false,
        error: error instanceof Error ? error.message : '送信に失敗しました',
        stage: 'failed'
      };
      
      setResult(errorResult);
      return errorResult;
      
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusMessage = (): string => {
    switch (currentStage) {
      case 'submitting':
        return 'MONO-X Oneに送信中...';
      case 'verifying':
        return 'データ反映確認中...';
      case 'completed':
        return 'データ反映確認完了';
      case 'failed':
        return '送信に失敗しました';
      default:
        return '';
    }
  };

  return {
    isSubmitting,
    result,
    currentStage,
    submitToMonoXOne,
    clearResult: () => setResult(null),
    getStatusMessage
  };
}; 